import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { z } from 'zod';
import { useExperimentFlags } from '@hungryroot/experiments';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { log, logExceptionOnce } from '@hungryroot/o11y';
import { getStripeElementsOptions, toStripeAmount } from '../../stripe';

const STRIPE_KEY = z
  .string({
    invalid_type_error:
      'Unable to parse PUBLIC_STRIPE_KEY. Included in env file?',
  })
  .parse(process.env.PUBLIC_STRIPE_KEY);

// Lazy singleton: loadStripe() fetches js.stripe.com, which some content
// blockers reject. Deferring the call to first render (rather than module
// eval) keeps that request off pages that pull this barrel but never render
// Stripe UI. The promise itself must never reject: <Elements stripe={...}>
// chains its own .then() onto it with no rejection handler, so a rejected
// promise here becomes an unhandled rejection inside Elements regardless of
// any .catch() we attach on the side. Instead we fold the rejection into a
// resolved `null`, which Elements accepts (it just stays unloaded, same
// outcome a failed load already produces). The singleton also guarantees a
// stable promise identity across re-renders, which Elements requires.
let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (stripePromise === null) {
    stripePromise = loadStripe(STRIPE_KEY).catch((error: unknown) => {
      logExceptionOnce(error, { key: 'StripeElements.loadStripe' });
      return null;
    });
  }
  return stripePromise;
}

const PaymentElementFlowContext = createContext(false);

/**
 * Whether the caller is currently rendered inside the Payment Element flow
 * (feature flag on AND Exp 243 variant), as opposed to the legacy
 * CardElement flow. Lets descendants branch without re-reading the flag or
 * experiment directly.
 */
export function useIsPaymentElementFlow(): boolean {
  return useContext(PaymentElementFlowContext);
}

type Props = {
  children: ReactNode;
  /**
   * Display dollars (e.g. `10.99`); converted to Stripe minor units
   * internally. Required for 'payment' mode when the payment-element flag
   * is enabled.
   */
  amount?: number;
  /**
   * Only relevant for the checkout flow, where we want to charge now and
   * save the method for future off-session billing.
   */
  setupFutureUsage?: 'off_session';
  /**
   * Identifies the calling surface (e.g. 'checkout', 'gift',
   * 'account-settings') on the one-shot exposure log fired once the
   * flag/experiment resolve. Optional so callers that don't have an obvious
   * scope aren't forced to invent one.
   */
  scope?: string;
};

export function StripeElements({
  children,
  amount,
  setupFutureUsage,
  scope,
}: Props) {
  const isPaymentElementEnabled = useFeatureFlag(
    'web.feature-flag.update-stripe-payment-element.enabled'
  );
  const hasLoggedExposureRef = useRef(false);

  // Fail open to the legacy flow until the flag resolves to true, so a
  // missing or failed flag fetch can never blank a payment surface. The
  // experiment check lives in a child component so users are only enrolled
  // in Exp 243 once the kill-switch flag is on. Only log exposure here when
  // the flag is resolved and off — the flag-on case defers to
  // ExperimentGatedElements, which also knows the variant.
  useEffect(() => {
    if (
      isPaymentElementEnabled === undefined ||
      isPaymentElementEnabled === true ||
      hasLoggedExposureRef.current
    ) {
      return;
    }
    hasLoggedExposureRef.current = true;
    log('Stripe elements flow resolved', {
      key: 'stripeElements.flowResolved.flag_off',
      scope,
      event: 'payment-element-exposure',
      paymentElementFlow: false,
      flagEnabled: false,
      isVariant: false,
    });
  }, [isPaymentElementEnabled, scope]);

  if (isPaymentElementEnabled !== true) {
    return (
      <PaymentElementFlowContext.Provider value={false}>
        <Elements stripe={getStripePromise()}>{children}</Elements>
      </PaymentElementFlowContext.Provider>
    );
  }

  return (
    <ExperimentGatedElements
      amount={amount}
      setupFutureUsage={setupFutureUsage}
      scope={scope}
    >
      {children}
    </ExperimentGatedElements>
  );
}

function ExperimentGatedElements({
  children,
  amount,
  setupFutureUsage,
  scope,
}: Props) {
  const { stripePaymentUpdatesExp243, isLoading } = useExperimentFlags(243);
  const hasLoggedExposureRef = useRef(false);

  useEffect(() => {
    if (isLoading || hasLoggedExposureRef.current) {
      return;
    }
    hasLoggedExposureRef.current = true;
    const isVariant = stripePaymentUpdatesExp243?.isVariant === true;
    log('Stripe elements flow resolved', {
      key: 'stripeElements.flowResolved.flag_on',
      scope,
      event: 'payment-element-exposure',
      paymentElementFlow: isVariant,
      flagEnabled: true,
      isVariant,
    });
  }, [isLoading, stripePaymentUpdatesExp243, scope]);

  // Control (and still-loading) users stay on the legacy flow. The key
  // remounts the provider when the variant resolves, because Stripe ignores
  // post-mount changes to mode/amount options.
  if (isLoading || stripePaymentUpdatesExp243?.isVariant !== true) {
    return (
      <PaymentElementFlowContext.Provider value={false}>
        <Elements key="legacy" stripe={getStripePromise()}>
          {children}
        </Elements>
      </PaymentElementFlowContext.Provider>
    );
  }

  const paymentElementOptions = getStripeElementsOptions({
    amount: typeof amount === 'number' ? toStripeAmount(amount) : undefined,
    setupFutureUsage,
  });

  return (
    <PaymentElementFlowContext.Provider value={true}>
      <Elements
        key="payment-element"
        stripe={getStripePromise()}
        options={paymentElementOptions}
      >
        {children}
      </Elements>
    </PaymentElementFlowContext.Provider>
  );
}
